import { useState } from 'react';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExpenseCategoryListSchema,
  ExpenseCategorySchema,
  type AccountDto,
  type ExpenseCategoryDto,
} from '@taqon/contracts';
import { getData, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { AccountPicker } from '../finance/lookups';

const CATEGORIES = '/api/v1/finance/expense-categories';

/**
 * Expense categories (FRD-FIN-083): Finance maps each category an employee may claim under to a
 * COA posting account. The account's dimension flags then apply to every claim line in it
 * (FRD-FIN-084). No account is inferred from a category's name.
 */
export function ExpenseCategoriesPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'finance.expense_category:create');
  const canChange = hasCapability(session.data, 'finance.expense_category:change');
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => getData(CATEGORIES, ExpenseCategoryListSchema),
  });
  const [editing, setEditing] = useState<ExpenseCategoryDto | 'new' | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [account, setAccount] = useState<AccountDto | null>(null);
  const [accountId, setAccountId] = useState('');
  const [status, setStatus] = useState('active');
  const open = (category: ExpenseCategoryDto | 'new') => {
    setEditing(category);
    setCode(category === 'new' ? '' : category.code);
    setName(category === 'new' ? '' : category.name);
    setAccount(null);
    setAccountId(category === 'new' ? '' : category.accountId);
    setStatus(category === 'new' ? 'active' : category.status);
  };
  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? postJson(
            CATEGORIES,
            { code: code.trim(), name: name.trim(), accountId },
            ExpenseCategorySchema,
            session.data?.csrfToken ?? '',
          )
        : patchJson(
            `${CATEGORIES}/${editing?.id ?? ''}`,
            { name: name.trim(), accountId, status },
            ExpenseCategorySchema,
            session.data?.csrfToken ?? '',
            editing?.version ?? 0,
          ),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    },
  });
  const requirements = (category: ExpenseCategoryDto) =>
    [
      category.requires.costCenter ? 'Cost centre' : null,
      category.requires.project ? 'Project' : null,
      category.requires.client ? 'Client' : null,
    ]
      .filter((part) => part !== null)
      .join(', ') || '—';
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Expense categories"
        description="The categories employees claim under, each mapped to its COA account."
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                open('new');
              }}
            >
              New category
            </Button>
          )
        }
      />
      <Section>
        {categories.isPending && <LoadingState label="Loading categories" />}
        {categories.isError && <ErrorState message={categories.error.message} />}
        {categories.data?.length === 0 && (
          <EmptyState title="No categories yet" detail="Map the first category to its account." />
        )}
        {categories.data !== undefined && categories.data.length > 0 && (
          <Table size="small" aria-label="Expense categories">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Line requires</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.data.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.code}</TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    {category.accountCode ?? '—'} {category.accountName ?? ''}
                  </TableCell>
                  <TableCell>{requirements(category)}</TableCell>
                  <TableCell>
                    <StatusChip status={category.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => {
                          open(category);
                        }}
                      >
                        Change
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
      {editing !== null && (
        <FormDialog
          open
          title={editing === 'new' ? 'New expense category' : `Change ${editing.code}`}
          submitLabel="Save"
          canSubmit={
            name.trim() !== '' &&
            accountId !== '' &&
            (editing !== 'new' || /^[A-Z][A-Z0-9_-]{0,19}$/.test(code.trim()))
          }
          submitting={save.isPending}
          error={save.isError ? save.error.message : null}
          onClose={() => {
            setEditing(null);
          }}
          onSubmit={() => {
            save.mutate();
          }}
        >
          {editing === 'new' && (
            <TextField
              label="Code"
              required
              value={code}
              helperText="Upper-case letters, digits, - or _"
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
              }}
            />
          )}
          <TextField
            label="Name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <AccountPicker
            value={account}
            helperText={
              account === null && editing !== 'new'
                ? `Currently ${editing.accountCode ?? ''} ${editing.accountName ?? ''}`
                : undefined
            }
            onChange={(value) => {
              setAccount(value);
              setAccountId(value?.id ?? (editing === 'new' ? '' : editing.accountId));
            }}
          />
          {editing !== 'new' && (
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          )}
        </FormDialog>
      )}
    </Stack>
  );
}
